/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles, TextField} from '@material-ui/core';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {info} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import Tooltip from '../components/tooltip/Tooltip';
import {useStateRef} from '../hooks/useStateRef';
import {toggleThemeStyle} from '../theme/helpers';
import {dlsColor} from '../theme/theme';
import {resetValue} from '../utils/ComponentUtils';
import {getPageProxy, PaginationChange} from './IonGridUtils';
import usePageConstraints, {hasConstraintExceeded} from './usePageConstraints';

export interface PageConstraintsProps {
  constraint?: number;
  entity?: string;
};

// defines the props definitions for GoToPage component...
export interface GoToPageProps {
  gridApi: GenericObject,
  paging?: UsePaginationResult,
  startRow: number,
  pageSize: number,
  totalPage: number,
  totalResultCount: number,
  loading?: boolean,
  isCurrentTheme?: boolean,
  pageConstraintProps?: PageConstraintsProps,
  onManualPaginationChange?: Function
};

// defines the style definitions for GoToPage component...
const useStyles = makeStyles((theme) => ({
  root : {
    display        : 'inline-flex',
    alignItems     : 'center',
    justifyContent : 'flex-end'
  },
  input : toggleThemeStyle({
    width       : '40px',
    height      : '18px',
    border      : `1px solid ${dlsColor('grey4')}`,
    marginRight : '4px'
  }, {
    width          : '48px',
    border         : `1px solid ${dlsColor('grey5')}`,
    height         : '20px',
    display        : 'flex',
    alignItems     : 'center',
    borderWidth    : '1px',
    marginRight    : '4px',
    borderRadius   : '4px',
    justifyContent : 'center'
  }),
  textRoot : toggleThemeStyle({
    height         : '100%',
    justifyContent : 'center'
  }, {
    height         : '100%',
    justifyContent : 'center'
  }),
  underline : (props: {isCurrentTheme: boolean}) => ({
    margin      : '0 !important',
    padding     : '0 !important',
    fontSize    : '14px !important',
    '& > input' : {
      padding   : '0 !important',
      textAlign : 'center'
    },
    '&&&:before' : {
      border : 'none'
    },
    '&&:after' : {
      border : 'none'
    },
    ...(props.isCurrentTheme && {
      color         : dlsColor('grey2'),
      fontSize      : '12px !important',
      position      : 'sticky',
      textAlign     : 'center',
      fontFamily    : 'Inter !important',
      fontWeight    : 600,
      lineHeight    : '16px',
      letterSpacing : '0.5px'
    })
  }),
  totalPagesText : {
    color      : dlsColor('grey2'),
    fontSize   : '12px',
    fontFamily : 'Inter',
    marginTop  : '3px',
    fontWeight : 500,
    lineHeight : '16px'
  }
}));

// private function to calculate the current page by using start index of the grid and page size
const getPage = (start: number, size: number) => Math.floor(start / size) + 1;

/**
 * Defining the GoToPage component. This component allows the user to Jump to the
 * available pages in the grid within the text field.
 *
 * ```typescript
 *
 *  import {GoToPage} from '@ion/dls/src/grid/';
 *
 *  <GoToPage gridApi{api}
 *    startRow={startIndex}
 *    pageSize={size}
 *    totalPage={totalPage} />
 *
 * ```
 */
export const GoToPage = memo(({
  gridApi,
  paging,
  startRow,
  pageSize,
  totalPage,
  totalResultCount,
  pageConstraintProps,
  onManualPaginationChange,
  isCurrentTheme = false,
  loading = false
}: GoToPageProps) => {
  const classes                                     = useStyles({isCurrentTheme});
  const [constraintExceeded, setConstraintExceeded] = useState(false);

  const [
    currentPage,
    setCurrentPage,
    currentPageRef
  ] = useStateRef(getPage(startRow, pageSize));

  const isConstraintExceeded = usePageConstraints({
    pageConstraint : pageConstraintProps?.constraint,
    page           : currentPageRef.current,
    pageSize
  });

  useEffect(() => {
    setCurrentPage(getPage(startRow, pageSize));
  }, [startRow, pageSize]);

  const goToPage = useCallback(debounce(() => {
    const page          = currentPageRef.current - 1;
    const proxy         = getPageProxy(gridApi, totalResultCount);
    const isNotSamePage = paging ? (paging?.page !== currentPageRef.current) : (proxy.currentPage !== page);
    const hasConstraint = hasConstraintExceeded(
      currentPageRef.current,
      pageSize,
      pageConstraintProps?.constraint
    );
    if (Number.isInteger(page) && isNotSamePage && !hasConstraint) {
      gridApi.paginationGoToPage(page);
      onManualPaginationChange?.(PaginationChange.PAGE_CHANGE);
      paging?.gotoPage(page + 1);
      setConstraintExceeded(false);
    } else if (hasConstraint) {
      setCurrentPage(getPage(startRow, pageSize));
      resetValue({
        fnCall       : setConstraintExceeded,
        initialValue : true,
        resetValue   : false,
        delay        : 3000
      });
    }
  }, 1500), [gridApi, paging, pageSize, pageConstraintProps?.constraint, startRow]);

  const onSetPage = useCallback(({target: {value}}) => {
    const page = parseInt(value);
    if (isNaN(page) || (page > 0 && page <= totalPage)) {
      info('page changing to:', page);
      setCurrentPage(page);
      goToPage();
    }
  }, [totalPage, goToPage]);

  const onBlur = useCallback(() => {
    if (!currentPageRef.current) {
      setCurrentPage(getPage(startRow, pageSize));
    }
  }, [startRow, pageSize]);

  return (
    <div className={classes.root}>
      <div className={classes.input}>
        <Tooltip title={translate('grids:pagination.constraint', {
          pageConstraint : pageConstraintProps?.constraint,
          entity         : pageConstraintProps?.entity
        })}
        isOpen={constraintExceeded}
        renderOnlyChildren={!constraintExceeded && !isConstraintExceeded}>
          <TextField label=''
            type='number'
            disabled={loading}
            value={currentPage}
            onChange={onSetPage}
            onBlur={onBlur}
            classes={{root: classes.textRoot}}
            InputProps={{
              classes : {
                input     : classes.underline,
                underline : classes.underline
              }
            }} />
        </Tooltip>
      </div>
      <span className={isCurrentTheme ? classes.totalPagesText : ''}>
        / {totalPage || '-'}
      </span>
    </div>
  );
});

export default GoToPage;
